import { Fragment, useEffect, useMemo, useState } from 'react'
import { fetchAgentSkills, applySkillAction, disableAgent } from '../api/team'
import {
  EXPERTISE_AT_RISK_JOB_KEY, fetchInsightReportStatus, launchInsightReport, fetchInsightReport,
} from '../api/insights'
import { getTokenPayload } from '../api/security'
import { useReportJob } from '../hooks/useReportJob'
import ConfirmDialog from './ConfirmDialog'
import AssignAgentSkillModal from './AssignAgentSkillModal'
import SkillPill from './SkillPill'
import SkillEvidence from './SkillEvidence'
import Icon from './Icon'
import { summaryTypeKeys } from '../utils/skillSummary'
import { useIsMobile } from '../hooks/useIsMobile'
import styles from './AgentSkillsModal.module.css'

// Avatar palette (mirrors TeamMemberRow so the header avatar matches the list tile).
const AVATAR_PALETTE = [
  '#E05A4E', '#E08A4E', '#E0C44E', '#6DBE6D',
  '#4E9EE0', '#7B6DE0', '#C44EBE', '#4EC4C4',
]

function avatarColor(id) {
  const seed = String(id)
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length]
}

function formatDate(iso) {
  if (!iso) return '—'
  // "last_used" is a calendar date, not an instant. A bare YYYY-MM-DD is parsed as
  // UTC midnight by `new Date`, which then shifts a day when shown in a timezone west
  // of UTC. Build it as a local date so the calendar day is preserved everywhere.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

// metric_name → human label for the header cases line.
const METRIC_LABELS = {
  agent_ticket_count: 'Cases resolved',
  agent_assist_count: 'Cases assisted',
  agent_res_time: 'avg. resolution',
}
const METRIC_ORDER = ['agent_ticket_count', 'agent_assist_count', 'agent_res_time']

// Classification → SkillPill colour variant.
function pillVariant(type) {
  return (type || 'general').toLowerCase()
}

// Ordinal ranks for the non-lexical sort columns. Level is ordinal text, so an
// alphabetical sort would be wrong (Advanced before Beginner); Category sorts in
// the backend _VALID_SKILL_TYPES order (Core→Technical→Process→General).
const LEVEL_RANK = { None: 0, Beginner: 1, Intermediate: 2, Advanced: 3, Expert: 4 }
const CATEGORY_RANK = { Core: 0, Technical: 1, Process: 2, General: 3 }

// Per-column comparators, each returning ascending order; the active direction is
// applied on top (desc = negate). Only the active skills are sorted — proposed
// "New" rows are pinned above the result and never run through these.
const SORT_FNS = {
  name: (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  // No name tie-break: equal categories keep their original order (stable sort),
  // consistent with the other columns.
  type: (a, b) => (CATEGORY_RANK[a.type] ?? 99) - (CATEGORY_RANK[b.type] ?? 99),
  level: (a, b) => (LEVEL_RANK[a.level] ?? -1) - (LEVEL_RANK[b.level] ?? -1),
  count: (a, b) => (a.count ?? 0) - (b.count ?? 0),
  last_used: (a, b) => String(a.last_used ?? '').localeCompare(String(b.last_used ?? '')),
}

/**
 * AgentSkillsModal — agent skill detail overlay opened from a Team member tile.
 *
 * Self-fetching (like CaseEvidence): loads GET /skills/agents/{agentId} on mount.
 * Header identity comes from the already-loaded `member`; the skills table,
 * proposed skills, summary and metrics come from the fetch.
 *
 * The `mode` prop is the seam for the future Review variant. The per-skill
 * evidence-case expander is available in both modes; what differs is the actions:
 *   - 'details' (default): every row's actions are up / down / lock / trash.
 *   - 'review' (not built): proposed (pending) skills get approve / deny instead.
 *     See renderActions below.
 *
 * Props:
 *   agentId  {number|string} — agent id, passed to the API
 *   member   {object}        — the member tile data (name, initials, role, teams)
 *   mode     {'details'|'review'} — variant (default 'details')
 *   onClose  {function}      — called when the modal is dismissed
 *   onProposedResolved {function} — called with agentId each time a proposed skill
 *     leaves the review queue (approve / reject / stale-404 drop), so the parent can
 *     decrement that member's "N new skills" count.
 *   onAgentDisabled {function} — called with agentId after the member is disabled,
 *     so the parent can drop them from the list (the modal then closes itself).
 */
// This is the Teams tab's per-member skill modal, so it's gated specifically
// on team_skill_management (not org_skill_management, and not admin_right —
// admin grants no skill rights on its own) — except superadmin_right, which
// overrides every granular right tenant-wide. Without access, the whole
// Actions column (and the Assign Skill button) doesn't render at all —
// viewing the table and evidence stays open to everyone regardless.
function canActionSkills() {
  const rights = getTokenPayload()?.rights
  return Boolean(rights?.team_skill_management) || Boolean(rights?.superadmin_right)
}

export default function AgentSkillsModal({ agentId, member, mode = 'details', onClose, onProposedResolved, onAgentDisabled }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const canAction = canActionSkills()
  const isMobile = useIsMobile()

  // On-demand Expertise-At-Risk report, keyed by the member's NAME (the subject the
  // backend stores it under — see routes/insights.py's {{EMPLOYEE_NAME}} token). Gated
  // on the same team_skill_management right as the other actions in this modal, and on
  // having a name to key by; useReportJob makes no calls while `enabled` is false.
  const reportSubject = member?.name ?? ''
  const canReport = canAction && Boolean(reportSubject)
  const report = useReportJob({
    enabled: canReport,
    identity: `${EXPERTISE_AT_RISK_JOB_KEY}:${reportSubject}`,
    fetchStatus: () => fetchInsightReportStatus(EXPERTISE_AT_RISK_JOB_KEY, reportSubject),
    launch: () => launchInsightReport(EXPERTISE_AT_RISK_JOB_KEY, reportSubject),
    fetchBlob: () => fetchInsightReport(EXPERTISE_AT_RISK_JOB_KEY, reportSubject),
  })

  const [showNew, setShowNew] = useState(true)
  const [category, setCategory] = useState('all')

  // Active-skill sort: { key, dir } or null for the default (backend) order.
  // Clicking a header cycles none → asc → desc → none (a third click restores
  // the default order); clicking a different column starts fresh at asc.
  const [sort, setSort] = useState(null)

  function requestSort(key) {
    setSort(prev => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' }
      if (prev.dir === 'asc') return { key, dir: 'desc' }
      return null
    })
  }

  // Skill rows whose evidence-case section is expanded (review variant only).
  const [expandedEvidence, setExpandedEvidence] = useState(() => new Set())

  // Row keys with an action in flight — used to disable that row's buttons so a
  // single skill can't be double-submitted.
  const [busy, setBusy] = useState(() => new Set())

  // Pending confirmation, or null. Holds the ConfirmDialog props plus the action
  // to run if the user confirms (lock / unlock / delete all gate through here).
  const [confirm, setConfirm] = useState(null)

  // Disable-user request in flight / its failure message (shown in the footer).
  const [disabling, setDisabling] = useState(false)
  const [disableError, setDisableError] = useState(null)

  // Assign-skill overlay (assign one or more skills to this member).
  const [assignOpen, setAssignOpen] = useState(false)

  // The modal mounts fresh per agent (agentId is stable for its lifetime), so the
  // initial state (loading/null) is the reset — no synchronous setState needed.
  // `active` guards against a late response after unmount.
  useEffect(() => {
    let active = true
    fetchAgentSkills(agentId)
      .then(d => { if (active) setData(d) })
      .catch(e => { if (active) setError(e.message) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [agentId])

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose()
  }

  // Primary team for the role line (falls back to the first team).
  const primaryTeam = member?.teams?.find(t => t.is_primary) ?? member?.teams?.[0]

  // Distinct categories present, for the dropdown.
  const categories = useMemo(() => {
    if (!data) return []
    const set = new Set()
    data.skills?.forEach(s => s.type && set.add(s.type))
    data.proposed_skills?.forEach(s => s.type && set.add(s.type))
    return [...set].sort()
  }, [data])

  // Unified row set: proposed (when "Show New Skills" on) tagged isProposed and
  // pinned at the top, then the active skills below; both filtered by the category
  // dropdown. Only the active skills are sorted — proposed "New" rows have no
  // level/count/last-used to order by, so they always rank highest (top of list)
  // regardless of the active sort or its direction.
  const rows = useMemo(() => {
    if (!data) return []
    const byCategory = r => category === 'all' || r.type === category
    const proposed = (showNew ? (data.proposed_skills ?? []) : [])
      .map(s => ({ ...s, isProposed: true }))
      .filter(byCategory)
    let active = (data.skills ?? [])
      .map(s => ({ ...s, isProposed: false }))
      .filter(byCategory)
    if (sort) {
      const cmp = SORT_FNS[sort.key]
      const sign = sort.dir === 'desc' ? -1 : 1
      active = [...active].sort((a, b) => sign * cmp(a, b))
    }
    return [...proposed, ...active]
  }, [data, showNew, category, sort])

  const isReview = mode === 'review'

  // Stable identity for a row, shared by its React key and the expanded-evidence set
  // (proposed vs active are distinct even when they share a skill_id).
  function rowKey(row) {
    return `${row.isProposed ? 'new' : 'sk'}-${row.skill_id ?? row.name}`
  }

  function toggleEvidence(row) {
    const key = rowKey(row)
    setExpandedEvidence(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  // Patch a single active skill in place (level / lock state) after an action.
  function patchSkill(skillId, patch) {
    setData(prev => prev && {
      ...prev,
      skills: (prev.skills ?? []).map(s => s.skill_id === skillId ? { ...s, ...patch } : s),
    })
  }

  // Drop a skill from the active list after a successful delete. The backend also
  // records it as 'rejected' so it won't be re-proposed; nothing more to show here.
  function dropSkill(skillId) {
    setData(prev => prev && {
      ...prev,
      skills: (prev.skills ?? []).filter(s => s.skill_id !== skillId),
    })
  }

  // Promote a proposed skill into the active list after a successful approve. The
  // backend created the agent_skill at Beginner; build the active row from the
  // proposed row plus the response's level/locked. Once it moves, the `rows` memo
  // re-renders it as an active skill, so its actions switch to up/down/lock/trash.
  // count/last_used aren't known until the next fetch — show neutral placeholders.
  function approveSkill(row, res) {
    setData(prev => prev && {
      ...prev,
      proposed_skills: (prev.proposed_skills ?? []).filter(s => s.skill_id !== row.skill_id),
      skills: [...(prev.skills ?? []), {
        name: row.name,
        skill_id: row.skill_id,
        type: row.type,
        level: res?.level ?? 'Beginner',
        locked: res?.locked ?? false,
        status: 'Active',
        count: 0,
        last_used: null,
      }],
    })
  }

  // Drop a proposed skill after a successful reject — the backend flips it to
  // 'rejected'; the card simply disappears from the list.
  function dropProposed(skillId) {
    setData(prev => prev && {
      ...prev,
      proposed_skills: (prev.proposed_skills ?? []).filter(s => s.skill_id !== skillId),
    })
  }

  // Silent resync after a stale-proposal 404 (see runAction). Best-effort: the
  // local drop already corrected the immediate view; this pulls the authoritative
  // state (e.g. surfaces a skill another reviewer approved into the active list).
  // No loading flip and failures are swallowed so it can't disrupt the open modal.
  function refetch() {
    fetchAgentSkills(agentId)
      .then(d => setData(d))
      .catch(() => {})
  }

  // Fire a skill action, mark the row busy while it's in flight, and reflect the
  // result in the UI. up/down apply the returned level; lock/unlock the returned
  // locked flag; delete removes the row. Failures surface via an alert.
  async function runAction(row, action) {
    const key = rowKey(row)
    if (busy.has(key)) return
    setBusy(prev => new Set(prev).add(key))
    try {
      const res = await applySkillAction(agentId, row.skill_id, action)
      if (action === 'up' || action === 'down') {
        if (res?.level) patchSkill(row.skill_id, { level: res.level })
      } else if (action === 'lock' || action === 'unlock') {
        patchSkill(row.skill_id, { locked: res?.locked ?? action === 'lock' })
      } else if (action === 'delete') {
        dropSkill(row.skill_id)
      } else if (action === 'approve') {
        approveSkill(row, res)
        onProposedResolved?.(agentId)   // parent decrements this member's new-skill count
      } else if (action === 'reject') {
        dropProposed(row.skill_id)
        onProposedResolved?.(agentId)
      }
    } catch (e) {
      // A 404 on approve/reject means the proposal is already gone — approved or
      // rejected by another reviewer, or a duplicate click. Treat it as stale:
      // drop the row and resync, rather than surfacing an error the user can't act on.
      if (e.status === 404 && (action === 'approve' || action === 'reject')) {
        dropProposed(row.skill_id)
        onProposedResolved?.(agentId)   // the proposal is gone too — keep the parent count in step
        refetch()
      } else {
        window.alert(`Could not ${action} “${row.name}”: ${e.message}`)
      }
    } finally {
      setBusy(prev => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  // Lock/unlock and delete require explicit confirmation: open the in-product
  // ConfirmDialog, and only fire the action once the user confirms. `who` is the
  // agent's display name (falls back when the member tile lacks one).
  const who = member?.name ?? 'this team member'

  function confirmLockToggle(row, isLocked) {
    if (isLocked) {
      setConfirm({
        title: 'Unlock skill',
        message: `Unlock skill “${row.name}” for ${who}.`,
        confirmLabel: 'Unlock Skill',
        variant: 'primary',
        onConfirm: () => { setConfirm(null); runAction(row, 'unlock') },
      })
    } else {
      setConfirm({
        title: 'Lock skill',
        message: `This skill will be locked for ${who}. GaussIQ will not automatically `
          + `adjust this skill for ${who} in the future. It may be unlocked at any time `
          + `and GaussIQ will start to evaluate the skill again.`,
        confirmLabel: 'Lock Skill',
        variant: 'primary',
        onConfirm: () => { setConfirm(null); runAction(row, 'lock') },
      })
    }
  }

  function confirmDelete(row) {
    setConfirm({
      title: 'Remove skill',
      message: `This skill will be removed from ${who}. GaussIQ will not suggest the skill `
        + `in the future for ${who}. You can manually re-add this skill at any time by `
        + `selecting “Assign Skill” and choosing it from the list.`,
      confirmLabel: 'Remove Skill',
      variant: 'danger',
      onConfirm: () => { setConfirm(null); runAction(row, 'delete') },
    })
  }

  // Disable user: sets the agent's active flag to false. Gated through the same
  // ConfirmDialog as the skill actions; on success the parent drops the member
  // from the list and the modal closes.
  function confirmDisable() {
    setConfirm({
      title: 'Disable user',
      message: `${who} will be marked inactive and removed from the team list. `
        + `They will not be re-activated by roster syncs from your connected systems.`,
      confirmLabel: 'Disable User',
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null)
        setDisabling(true)
        setDisableError(null)
        try {
          await disableAgent(agentId)
          onAgentDisabled?.(agentId)
          onClose()
        } catch (e) {
          setDisableError(e.message || 'Could not disable this user.')
          setDisabling(false)
        }
      },
    })
  }

  // Approve / reject a proposed ("New") skill — confirmed through the same
  // ConfirmDialog as delete/lock. Approve adds it to the active skills at Beginner;
  // reject discards the proposal and stops GaussIQ re-proposing it.
  function confirmApprove(row) {
    setConfirm({
      title: 'Approve skill',
      message: `This skill will be added to ${who}’s skills at Beginner level. `
        + `GaussIQ will assess and adjust it going forward.`,
      confirmLabel: 'Approve Skill',
      variant: 'primary',
      onConfirm: () => { setConfirm(null); runAction(row, 'approve') },
    })
  }

  function confirmReject(row) {
    setConfirm({
      title: 'Reject skill',
      message: `This proposed skill will be rejected for ${who}. GaussIQ will not add it `
        + `and will not propose it again for ${who}.`,
      confirmLabel: 'Reject Skill',
      variant: 'danger',
      onConfirm: () => { setConfirm(null); runAction(row, 'reject') },
    })
  }

  // Action cell. Only called when canAction is true — the whole Actions column
  // (header + cells) doesn't render at all otherwise, see the table below — so
  // there's no disabled state to handle here, just which controls apply.
  // The mode seam lives here: in review mode a proposed (pending) skill gets
  // approve / deny; otherwise (details, or an already-active skill) the
  // controls are up / down / lock / trash. Evidence cases are opened by
  // clicking the row itself (see the <tr> below), not by a button here.
  function renderActions(row) {
    if (isReview && row.isProposed) {
      const isBusy = busy.has(rowKey(row))
      return (
        <div className={styles.actions}>
          <button className={`${styles.iconBtn} ${styles.iconBtnApprove}`} title="Approve skill"
            disabled={isBusy}
            onClick={() => confirmApprove(row)} aria-label="Approve skill">
            <Icon name="check" />
          </button>
          <button className={`${styles.iconBtn} ${styles.iconBtnDeny}`} title="Reject skill"
            disabled={isBusy}
            onClick={() => confirmReject(row)} aria-label="Reject skill">
            <Icon name="close" />
          </button>
        </div>
      )
    }
    // Details mode: a proposed ("New") skill has no manual actions yet — those
    // arrive with the Review variant's approve / deny controls. Until then the
    // row stays inspectable (click it to open evidence) but un-actionable.
    if (row.isProposed) {
      return null
    }

    // Lock state comes from the GET payload's `locked` (and our own lock/unlock
    // response); the status heuristic is a fallback for older payloads. Open
    // padlock + grey when unlocked, closed padlock + highlight when locked.
    const isLocked = row.locked != null
      ? row.locked
      : Boolean(row.status && row.status !== 'Active')
    const isBusy = busy.has(rowKey(row))
    return (
      <div className={styles.actions}>
        <button className={styles.iconBtn} disabled={isBusy}
          title="Increase level"
          onClick={() => runAction(row, 'up')} aria-label="Increase level">
          <Icon name="up" />
        </button>
        <button className={styles.iconBtn} disabled={isBusy}
          title="Decrease level"
          onClick={() => runAction(row, 'down')} aria-label="Decrease level">
          <Icon name="down" />
        </button>
        <button
          className={`${styles.iconBtn} ${isLocked ? styles.iconBtnLocked : ''}`}
          title={isLocked ? 'Unlock skill' : 'Lock skill'} disabled={isBusy}
          onClick={() => confirmLockToggle(row, isLocked)}
          aria-label={isLocked ? 'Unlock skill' : 'Lock skill'}>
          <Icon name={isLocked ? 'lock' : 'lockOpen'} />
        </button>
        <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
          disabled={isBusy}
          title="Remove skill"
          onClick={() => confirmDelete(row)} aria-label="Remove skill">
          <Icon name="trash" />
        </button>
      </div>
    )
  }

  // Clickable, keyboard-accessible sort header. The chevron slot keeps a fixed
  // width whether or not it shows a glyph, so toggling sort never shifts the row.
  function sortHeader(key, label, thClass = '') {
    const active = sort?.key === key
    return (
      <th
        className={`${thClass} ${styles.sortable} ${active ? styles.sortActive : ''}`}
        onClick={() => requestSort(key)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); requestSort(key) }
        }}
        role="button"
        tabIndex={0}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <span className={styles.thLabel}>
          {label}
          <span className={styles.sortIcon} aria-hidden="true">
            {active && <Icon name={sort.dir === 'asc' ? 'up' : 'down'} />}
          </span>
        </span>
      </th>
    )
  }

  const summary = data?.skills_summary ?? {}
  const summaryPills = summaryTypeKeys(summary)

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Agent skills">

        {/* ── Header ─────────────────────────────── */}
        <div className={styles.header}>
          <div className={styles.identity}>
            <div className={styles.avatar} style={{ backgroundColor: avatarColor(member?.id) }}>
              {member?.initials}
            </div>
            <div>
              <div className={styles.name}>{member?.name}</div>
              <div className={styles.role}>
                {member?.role}{primaryTeam ? ` · ${primaryTeam.name}` : ''}
              </div>
            </div>
          </div>

          <div className={styles.headerStats}>
            {summary.Total && (
              <div className={styles.summaryLine}>
                <span className={styles.summaryTotal}>{summary.Total} total skills</span>
                {summaryPills.map(k => (
                  <span key={k} className={`${styles.summaryPill} ${styles[`summaryPill${k}`]}`}>
                    {summary[k]} {k}
                  </span>
                ))}
              </div>
            )}
            {data?.metrics && (
              <div className={styles.metricsLine}>
                {METRIC_ORDER
                  .map(name => data.metrics.find(m => m.metric_name === name))
                  .filter(Boolean)
                  .map((m, i) => (
                    <span key={m.metric_name}>
                      {i > 0 && <span className={styles.dot}> · </span>}
                      <span className={styles.metricValue}>{m.metric_value}</span>{' '}
                      {METRIC_LABELS[m.metric_name] ?? m.metric_name}
                    </span>
                  ))}
              </div>
            )}
          </div>

          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>

        {/* ── Skills panel: bordered container that detaches the "All Skills"
              toolbar + list from the modal page (lighter border, darker list). ─ */}
        <div className={styles.skillsPanel}>

        {/* ── Toolbar ────────────────────────────── */}
        <div className={styles.toolbar}>
          <h3 className={styles.toolbarTitle}>All Skills</h3>
          <div className={styles.toolbarControls}>
            <label className={styles.checkbox}>
              <input type="checkbox" checked={showNew} onChange={e => setShowNew(e.target.checked)} />
              Show New Skills
            </label>
            <select className={styles.select} value={category} onChange={e => setCategory(e.target.value)}>
              <option value="all">All Categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {canAction && (
              <button
                className={styles.assignBtn}
                onClick={() => setAssignOpen(true)}
              >
                Assign Skill
              </button>
            )}
          </div>
        </div>

        {/* ── Body ───────────────────────────────── */}
        <div className={styles.body}>
          {loading && (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Loading skills…</p>
            </div>
          )}

          {error && !loading && (
            <div className={styles.errorState}>Failed to load skills: {error}</div>
          )}

          {data && !loading && isMobile && (
            <div className={styles.cardList}>
              {rows.length === 0 ? (
                <div className={styles.empty}>No skills match your filters.</div>
              ) : rows.map(row => {
                const key = rowKey(row)
                // Evidence can only be fetched for rows with a skill_id; those rows
                // become clickable (anywhere but the actions area) to toggle it.
                const canExpand = row.skill_id != null
                const open = expandedEvidence.has(key)
                return (
                  <div className={styles.skillCard} key={key}>
                    <div
                      className={canExpand ? styles.clickable : ''}
                      onClick={canExpand ? () => toggleEvidence(row) : undefined}
                      onKeyDown={canExpand ? e => {
                        if (e.target !== e.currentTarget) return
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleEvidence(row) }
                      } : undefined}
                      role={canExpand ? 'button' : undefined}
                      tabIndex={canExpand ? 0 : undefined}
                      aria-expanded={canExpand ? open : undefined}>
                      <div className={styles.skillCardHeader}>
                        <span className={styles.skillName}>{row.name}</span>
                        {row.isProposed
                          ? <span className={styles.newBadge}>New</span>
                          : <span className={styles.level}>{row.level}</span>}
                      </div>
                      <SkillPill label={row.type} variant={pillVariant(row.type)} />
                      <div className={styles.skillCardMeta}>
                        <div className={styles.skillCardField}>
                          <span className={styles.skillCardFieldLabel}>90-Day Count</span>
                          <span className={styles.numeric}>{row.isProposed ? '—' : row.count}</span>
                        </div>
                        <div className={styles.skillCardField}>
                          <span className={styles.skillCardFieldLabel}>Last Used</span>
                          <span className={styles.muted}>{row.isProposed ? '—' : formatDate(row.last_used)}</span>
                        </div>
                      </div>
                    </div>
                    {canAction && (
                      <div className={styles.skillCardActions} onClick={e => e.stopPropagation()}>
                        {renderActions(row)}
                      </div>
                    )}
                    {open && (
                      <div className={styles.skillCardEvidence}>
                        <SkillEvidence agentId={agentId} skillId={row.skill_id} skillName={row.name} />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {data && !loading && !isMobile && (
            <table className={styles.table}>
              <thead>
                <tr>
                  {sortHeader('name', 'Skill')}
                  {sortHeader('type', 'Category')}
                  {sortHeader('level', 'Level')}
                  {sortHeader('count', '90-Day Count')}
                  {sortHeader('last_used', 'Last Used')}
                  {/* No team_skill_management (and not a superadmin): the whole
                      column is gone, not just its buttons — the colSpans below
                      track this column count. */}
                  {canAction && <th className={styles.actionsCol}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={canAction ? 6 : 5} className={styles.empty}>No skills match your filters.</td></tr>
                ) : rows.map(row => {
                  const key = rowKey(row)
                  // Evidence can only be fetched for rows with a skill_id; those rows
                  // become clickable (anywhere but the actions cell) to toggle it.
                  const canExpand = row.skill_id != null
                  const open = expandedEvidence.has(key)
                  return (
                    <Fragment key={key}>
                      <tr
                        className={canExpand ? styles.clickable : ''}
                        onClick={canExpand ? () => toggleEvidence(row) : undefined}
                        onKeyDown={canExpand ? e => {
                          // Only the row itself toggles; ignore keys bubbling up from a
                          // focused action button (those activate the button, not the row).
                          if (e.target !== e.currentTarget) return
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleEvidence(row) }
                        } : undefined}
                        role={canExpand ? 'button' : undefined}
                        tabIndex={canExpand ? 0 : undefined}
                        aria-expanded={canExpand ? open : undefined}>
                        <td className={styles.skillName}>{row.name}</td>
                        <td><SkillPill label={row.type} variant={pillVariant(row.type)} /></td>
                        <td>
                          {row.isProposed
                            ? <span className={styles.newBadge}>New</span>
                            : <span className={styles.level}>{row.level}</span>}
                        </td>
                        <td className={styles.numeric}>{row.isProposed ? '—' : row.count}</td>
                        <td className={styles.muted}>{row.isProposed ? '—' : formatDate(row.last_used)}</td>
                        {/* Actions cell is a dead zone: button clicks fire their own
                            handler, then stop here so the row never toggles. */}
                        {canAction && <td onClick={e => e.stopPropagation()}>{renderActions(row)}</td>}
                      </tr>
                      {expandedEvidence.has(key) && (
                        <tr className={styles.evidenceRow}>
                          <td colSpan={canAction ? 6 : 5}>
                            <SkillEvidence agentId={agentId} skillId={row.skill_id} skillName={row.name} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          )}

          {/* Each row's evidence-case section is rendered inline as an expandable
              sub-row in the table above (SkillEvidence → CaseBadge / CaseEvidence),
              in both details and review modes. */}
        </div>

        </div>
        {/* ── end skills panel ─────────────────────── */}

        {/* ── Footer ─────────────────────────────── */}
        {/* Disable user sits bottom-left, the on-demand report action right-aligned.
            Each is gated on its own right; with neither (viewer lacks the right) the
            footer has nothing to show and is dropped entirely rather than leaving an
            empty bar. */}
        {(canAction || canReport) && (
          <div className={styles.footer}>
            {canAction && (
              <button
                type="button"
                className={styles.disableBtn}
                onClick={confirmDisable}
                disabled={disabling}
                aria-busy={disabling}
              >
                {disabling ? 'Disabling…' : 'Disable user'}
              </button>
            )}
            {disableError && (
              <span className={styles.reportError} role="alert">{disableError}</span>
            )}
            {canReport && report.error && (
              <span className={styles.reportError} role="alert">{report.error}</span>
            )}
            {canReport && (
              <ReportButton status={report.status} opening={report.opening} onClick={report.onAction} />
            )}
          </div>
        )}

      </div>

      {confirm && (
        <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)} />
      )}

      {assignOpen && (
        <AssignAgentSkillModal
          agentId={agentId}
          agentName={who}
          existingSkillIds={(data?.skills ?? []).map(s => s.skill_id)}
          onClose={() => setAssignOpen(false)}
          onAssigned={(promoted = 0) => {
            refetch()   // pull authoritative state (promoted proposals move to active)
            // Each promoted proposal left the review queue — keep the parent's
            // "N new skills" badge in step, one decrement per promotion.
            for (let i = 0; i < promoted; i++) onProposedResolved?.(agentId)
          }}
        />
      )}
    </div>
  )
}

/**
 * The Expertise-At-Risk report button, in whichever of its three states applies —
 * mirrors CaseDetails' PostMortemButton over the shared useReportJob machine:
 *
 *   none / error → "Generate Expertise-At-Risk Report"  starts a run (error = retry)
 *   generating   → "Generating Report"                  spinner, inert while polling
 *   done         → "Show Expertise-At-Risk Report"      opens the PDF in a new tab
 *
 * An error keeps the generate action rather than dead-ending: the message shows to
 * the left in the footer, and the obvious next move is to try again.
 */
function ReportButton({ status, opening, onClick }) {
  const generating = status === 'generating'
  const inert = generating || status === 'unknown' || opening

  let label = 'Generate Expertise-At-Risk Report'
  if (generating) label = 'Generating Report'
  else if (status === 'done') label = opening ? 'Opening…' : 'Show Expertise-At-Risk Report'

  return (
    <button
      type="button"
      className={styles.reportBtn}
      onClick={onClick}
      disabled={inert}
      aria-busy={generating || opening}
      aria-live="polite"
    >
      {generating ? <Icon name="refresh" className={styles.spinning} /> : <Icon name="document" />} {label}
    </button>
  )
}
